import { Grid, Table } from 'antd';
import type { ModalProps } from 'antd/lib/modal/Modal';
import { useCallback, useMemo, useState } from 'react';
import { type TFunction } from 'react-i18next';
import { ApplicationStatus } from '../../../../api/types';
import useSearchInDropDown from '../../../../components/useSearchInDropDown';
import { useThemeComponents } from '../../../../themes';
import { getButtonVariantsForModal } from '../../../../themes/default/Modal';
import renderSearchIcon from '../../../../utilities/renderSearchIcon';

const getStatusDescriptionList = (t: TFunction) =>
    (Object.keys(ApplicationStatus) as Array<keyof typeof ApplicationStatus>).map(appStatus => ({
        status: t(`applicationList:status.${appStatus}`),
        description: t(`dashboard:statusDescriptionModal.table.description.${appStatus}`),
    }));

const StatusDescriptionModal = ({
    t,
    onClose,
    visible,
}: {
    t: TFunction;
    onClose: ModalProps['onCancel'];
    visible: boolean;
}) => {
    const searchBox = useSearchInDropDown();
    const [filter, setFilter] = useState('');
    const [sort, setSort] = useState(1);
    const { theme, Button, Modal } = useThemeComponents();
    const statusDescriptionModal = 'dashboard:statusDescriptionModal';
    const screens = Grid.useBreakpoint();

    const [modalWidth, statusColWidth] = useMemo(() => {
        if (screens.xxl) {
            return ['40vw', '12vw'];
        }

        if (screens.lg) {
            return ['60vw', '20vw'];
        }

        if (screens.md) {
            return ['80vw', '30vw'];
        }

        return ['95vw', '30vw'];
    }, [screens.lg, screens.md, screens.xxl]);

    const statusDescriptionList = getStatusDescriptionList(t).filter(
        statusDescription => statusDescription.description
    );

    const sortedDescriptionList = useMemo(() => {
        let sortedDescriptionList = statusDescriptionList;
        if (filter) {
            const regex = new RegExp(filter, 'gi');
            sortedDescriptionList = sortedDescriptionList.filter(statusDescriptionList =>
                statusDescriptionList.status.match(regex)
            );
        }

        sortedDescriptionList =
            sort === 1
                ? sortedDescriptionList.toSorted((a, b) => a.status.localeCompare(b.status))
                : sortedDescriptionList.toSorted((a, b) => b.status.localeCompare(a.status));

        return sortedDescriptionList;
    }, [filter, sort, statusDescriptionList]);

    const onTableChange = useCallback((pagination, filters, sorter, extra) => {
        setFilter(filters.status || '');
        setSort(sorter.order === 'descend' ? -1 : 1);
    }, []);

    const closeModal = useCallback(
        e => {
            setFilter('');
            setSort(1);
            onClose(e);
        },
        [onClose]
    );

    const variants = getButtonVariantsForModal(theme);

    return (
        <Modal
            footer={[
                <Button key="cancel" onClick={closeModal} type={variants.cancel}>
                    {t(`${statusDescriptionModal}.close`)}
                </Button>,
            ]}
            onCancel={closeModal}
            open={visible}
            title={t(`${statusDescriptionModal}.title`)}
            width={modalWidth}
            destroyOnClose
        >
            <Table
                columns={[
                    {
                        dataIndex: 'status',
                        title: t(`${statusDescriptionModal}.table.column.status`),
                        key: 'status',
                        width: statusColWidth,
                        filterDropdown: searchBox.render('status'),
                        filterIcon: renderSearchIcon,
                        filterSearch: true,
                        sorter: true,
                    },
                    {
                        dataIndex: 'description',
                        title: t(`${statusDescriptionModal}.table.column.description`),
                        key: 'description',
                    },
                ]}
                dataSource={sortedDescriptionList}
                onChange={onTableChange}
                pagination={false}
                size="small"
                style={{ minHeight: '5rem' }}
            />
        </Modal>
    );
};

const useStatusDescriptionModal = (t: TFunction) => {
    const [visible, setVisible] = useState(false);

    const actions = useMemo(() => {
        const close = () => setVisible(false);

        return {
            open: () => setVisible(true),
            close,
        };
    }, [setVisible]);

    return {
        ...actions,
        render: () => <StatusDescriptionModal onClose={actions.close} t={t} visible={visible} />,
    };
};

export default useStatusDescriptionModal;
